import {
    ChatAppRequest, BlobClientUrlResponse, BlobUrlResponse, AllFilesUploadStatus, GetInfoResponse, GetLandingInfoResponse, FeedbackData,
    ActiveCitation, ExportRequest, StatusLogEntry, StatusLogResponse, GetTagsResponse, GetFoldersResponse, GptDeployment, UserData,
    PromptTemplate, ConversationHistory, HistoricConversation, FaqSummary, FaqItem, FileUpdateData, PermissionsGroup, PermissionsUser
} from "./models";
import { handleError } from "../toaster";

export function getHeaders(idToken: string | undefined): Record<string, string> {
    var headers: Record<string, string> = {
        "Content-Type": "application/json"
    };

    if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    };

    return headers;
};

export async function chatApi(idToken: string | undefined, request: ChatAppRequest, signal: AbortSignal): Promise<Response> {
    return await fetch(`/chat`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(request),
        signal: signal
    });
};

export async function downloadFileFromResponse(response: Response): Promise<void> {
    try {
        if (!response.ok) {
            const errorResponse = await response.json();
            throw new Error(errorResponse.error || 'An unknown error occurred');
        };

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;

        const contentDisposition = response.headers.get("Content-Disposition");
        const fileNameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);

        if (fileNameMatch && fileNameMatch.length >= 2) {
            a.download = fileNameMatch[1];
        }

        a.click();
        window.URL.revokeObjectURL(url);

    } catch (error) {
        handleError(error, "Error downloading file");
    }
};

export async function exportAnswer(idToken: string | undefined, request: ExportRequest): Promise<void> {
    try {
        const response = await fetch("/exportAnswer", {
            method: "POST",
            headers: getHeaders(idToken),
            body: JSON.stringify(request)
        });

        await downloadFileFromResponse(response);
    } catch (error) {
        handleError(error, "Error exporting answer");
    }
};

export function getCitationFilePath(citation: string): string {
    return `${encodeURIComponent(citation)}`;
};

export async function getBlobClientUrl(idToken: string | undefined): Promise<string> {
    const response = await fetch("/getBlobClientUrl", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: BlobClientUrlResponse = await response.json();
    return parsedResponse.url;
};

export async function uploadFile(
    idToken: string | undefined,
    file: File,
    status_log_entry: StatusLogEntry
): Promise<StatusLogResponse> {

    const formData = new FormData();
    formData.append('file', file);

    // Unpack and append each property of status_log_entry to formData
    formData.append('path', status_log_entry.path);
    formData.append('status', status_log_entry.status);
    formData.append('status_classification', status_log_entry.status_classification);
    formData.append('state', status_log_entry.state);

    formData.append('groups', JSON.stringify(status_log_entry.groups));
    formData.append('users', JSON.stringify(status_log_entry.users));
    formData.append('tags', JSON.stringify(status_log_entry.tags));

    // Get headers, but do not set 'Content-Type' manually for multipart/form-data
    // Let the browser set it with the correct boundary
    const headers = getHeaders(idToken);
    delete headers['Content-Type'];

    const response = await fetch("/uploadFile", {
        method: "POST",
        headers: headers,
        body: formData
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: StatusLogResponse = await response.json();
    const results: StatusLogResponse = { status: parsedResponse.status };
    return results;
};

export async function getBlobUrl(idToken: string | undefined, file_path: string): Promise<BlobUrlResponse> {
    const response = await fetch("/getBlobUrl", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ file_path })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: BlobUrlResponse = await response.json();
    return parsedResponse;
};

export async function getUploadStatus(
    idToken: string | undefined,
    skip: number = 0,
    limit: number = 25,
    fileStates: string[] = [],
    folders: string[] = [],
    tags: string[] = [],
    fileNameSearch: string = ""
): Promise<AllFilesUploadStatus> {
    const response = await fetch("/getUploadStatus", {
        method: "POST",
        headers: {
            ...getHeaders(idToken),
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            skip: skip,
            limit: limit,
            file_states: fileStates,
            folders: folders,
            tags: tags,
            file_name_search: fileNameSearch
        })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: AllFilesUploadStatus = await response.json();
    return parsedResponse;
};

export async function getInfoData(idToken: string | undefined): Promise<GetInfoResponse> {
    const response = await fetch("/getInfoData", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: GetInfoResponse = await response.json();
    return parsedResponse;
};

export async function getLandingInfoData(idToken: string | undefined): Promise<GetLandingInfoResponse> {
    const response = await fetch("/getLandingInfoData", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: GetLandingInfoResponse = await response.json();
    return parsedResponse;
};

export async function getCitationObj(idToken: string | undefined, citation: string): Promise<ActiveCitation> {
    const response = await fetch(`/getCitation`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ citation })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: ActiveCitation = await response.json();
    return parsedResponse;
};

export async function getAllFolders(idToken: string | undefined): Promise<GetFoldersResponse> {
    const response = await fetch("/getAllFolders", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {

        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse = await response.json();
    const results: GetFoldersResponse = { folders: parsedResponse };
    return results;
};

export async function getAllTags(idToken: string | undefined): Promise<GetTagsResponse> {
    const response = await fetch("/getAllTags", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {

        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse = await response.json();
    const results: GetTagsResponse = { tags: parsedResponse };
    return results;
};

export async function getGptDeployments(idToken: string | undefined): Promise<GptDeployment[]> {
    const response = await fetch("/getGptDeployments", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: GptDeployment[] = await response.json();
    return parsedResponse;
};

export async function setGptDeployment(idToken: string | undefined, deployment: GptDeployment): Promise<void> {
    const response = await fetch("/setGptDeployment", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(deployment)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw Error(errorResponse.error || "An unknown error occurred");
    }
};

export async function logout() {
    const response = await fetch("/logout", { method: "GET" });

    if (!response.ok) {
        throw new Error(response.statusText || "Unknown error");
    }
};

export async function getUserData(idToken: string | undefined): Promise<UserData> {
    const response = await fetch("/getUserData", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: UserData = await response.json();
    return parsedResponse;
};


export async function getUserPhoto(idToken: string | undefined): Promise<string> {
    const response = await fetch("/getUserPhoto", {
        method: "GET",
        headers: getHeaders(idToken),
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    return url;
};

export async function getPromptTemplates(idToken: string | undefined): Promise<PromptTemplate[]> {
    const response = await fetch("/getPromptTemplates", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: PromptTemplate[] = await response.json();
    return parsedResponse;
};

export async function upsertPromptTemplate(idToken: string | undefined, prompt_template: PromptTemplate): Promise<Response> {
    const response = await fetch("/upsertPromptTemplate", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ ...prompt_template })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function stopStream(idToken: string | undefined): Promise<Response> {
    const response = await fetch(`/stopStream`, {
        method: "POST",
        headers: getHeaders(idToken)
    });
    
    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function acceptTermsOfUse(idToken: string | undefined): Promise<Response> {
    const response = await fetch(`/termsOfUse`, {
        method: "POST",
        headers: getHeaders(idToken),
    });
    
    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function updateFiles(idToken: string | undefined, items: FileUpdateData[]): Promise<Response> {
    const response = await fetch("/updateFile", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ items })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function listFaqs(idToken: string | undefined): Promise<FaqSummary[]> {
    const response = await fetch(`/faqs`, {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: FaqSummary[] = await response.json();
    return parsedResponse;
};

export async function getFaq(idToken: string | undefined, faq_id: string): Promise<FaqItem> {
    const response = await fetch(`/faqs/${encodeURIComponent(faq_id)}`, {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: FaqItem = await response.json();
    return parsedResponse;
};

export async function updateFaq(idToken: string | undefined, faq: FaqItem): Promise<Response> {
    const response = await fetch(`/faqs/${encodeURIComponent(faq.faq_id)}`, {
        method: "PATCH",
        headers: getHeaders(idToken),
        body: JSON.stringify(faq)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function getConversationHistory(idToken: string | undefined, user_id: string): Promise<ConversationHistory> {
    const response = await fetch(`/getConversationHistory`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ user_id })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        return { history: [], error: errorResponse.error || 'An unknown error occurred' };
    };

    const parsedResponse = await response.json();
    const results: ConversationHistory = { history: parsedResponse };
    return results;
};

export async function getConversation(idToken: string | undefined, user_id: string, conversation_id: string): Promise<HistoricConversation> {
    const response = await fetch(`/getConversation`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ user_id, conversation_id })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse: HistoricConversation = await response.json();
    return parsedResponse;
};

export async function updateConversation(idToken: string | undefined, user_id: string, conversation_id: string, name?: string, archived?: boolean): Promise<Response> {
    const response = await fetch(`/updateConversation`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ user_id, conversation_id, name, archived })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function submitResponseFeedback(idToken: string | undefined, user_id: string, conversation_id: string, request_id: string, sentiment: string, comments?: string): Promise<Response> {
    const response = await fetch(`/updateConversation`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ user_id, conversation_id, request_id, sentiment, comments })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    }

    const parsedResponse = await response.json();
    return parsedResponse;
};

export async function getUserGroups(idToken: string | undefined): Promise<PermissionsGroup[]> {
    const response = await fetch("/getUserGroups", {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: PermissionsGroup[] = await response.json();
    return parsedResponse;
};

export async function getGroupNames(idToken: string | undefined, group_ids: string[]): Promise<PermissionsGroup[]> {
    const response = await fetch("/getGroupNames", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ group_ids })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: PermissionsGroup[] = await response.json();
    return parsedResponse;
};

export async function getUserNames(idToken: string | undefined, user_ids: string[]): Promise<PermissionsUser[]> {
    const response = await fetch("/getUserNames", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ user_ids })
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: PermissionsUser[] = await response.json();
    return parsedResponse;
};

export async function searchGroups(idToken: string | undefined, query: string): Promise<PermissionsGroup[]> {
    const url = new URL("/searchGroups", window.location.origin);
    url.searchParams.append("query", query);

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: PermissionsGroup[] = await response.json();
    return parsedResponse;
};

export async function searchUsers(idToken: string | undefined, query: string): Promise<PermissionsUser[]> {
    const url = new URL("/searchUsers", window.location.origin);
    url.searchParams.append("query", query);

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw new Error(errorResponse.error || 'An unknown error occurred');
    };

    const parsedResponse: PermissionsUser[] = await response.json();
    return parsedResponse;
};


export async function setUserFeedback(idToken: string | undefined, feedbackData: FeedbackData): Promise<void> {
    const response = await fetch("/setUserFeedback", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify(feedbackData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        throw Error(errorResponse.error || "An unknown error occurred");
    }
};

export async function getSpeechApi(idToken: string | undefined, text: string): Promise<string | null> {
    return await fetch("/speech", {
        method: "POST",
        headers: getHeaders(idToken),
        body: JSON.stringify({ text })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                return null;
            } else {
                handleError(response.json(), "Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
};